import React, { useCallback } from 'react';
import DetailRow from 'src/components/DetailRow';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import selectDocumentType from 'src/utils/selectDocumentType';
import { DocumentType } from 'src/utils/constants/clients';
import DocumentLink from '../DocumentLink/DocumentLink';
import DocumentsMenu from '../DocumentsMenu/DocumentsMenu';
import AddressType from '../AddressDialog/AddressEnums';

const AddressDetailBox = ({
  address,
  includeCountry = false,
  includeProofOfAddress = false,
  includeMunicipality = true,
  documentData = null
}) => {
  const { t } = useTranslation();

  const getAddressField = useCallback(
    (addressList, field) => {
      if (addressList && addressList[field]) {
        if (field === 'country') {
          return t(`COUNTRIES.${addressList[field]}`);
        }
        return addressList[field];
      }
      return t('COMMON.NO_DATA');
    },
    [address]
  );

  // Every other read in this component already tolerates a missing address
  // (getAddressField falls back to NO_DATA); this one did not, and crashed the
  // whole detail view for any contract without a location yet.
  const isPersonalAddress = address?.addressType === AddressType.HOME;
  const documentType = isPersonalAddress
    ? DocumentType.PERSONAL_ADDRESS_PROOF
    : DocumentType.LEGAL_ADDRESS_PROOF;

  return (
    <>
      <DetailRow
        name={t('COMMON.STREET')}
        value={getAddressField(address, 'street')}
      />

      {address && address.insideHomeNumber && (
        <DetailRow
          name={t('COMMON.INSIDE_HOME_NUMBER')}
          value={getAddressField(address, 'insideHomeNumber')}
        />
      )}

      <DetailRow
        name={t('COMMON.EXTERNAL_HOME_NUMBER')}
        value={getAddressField(address, 'externalHomeNumber')}
      />

      <DetailRow
        name={t('COMMON.COLONIA')}
        value={getAddressField(address, 'colonia')}
      />

      <DetailRow
        name={t('COMMON.CITY')}
        value={getAddressField(address, 'city')}
      />

      {includeMunicipality && (
        <DetailRow
          name={t('COMMON.MUNICIPALITY')}
          value={getAddressField(address, 'municipality')}
        />
      )}

      <DetailRow
        name={t('COMMON.STATE')}
        value={getAddressField(address, 'state')}
      />

      <DetailRow
        name={t('COMMON.ZIP_CODE')}
        value={getAddressField(address, 'zipCode')}
      />

      {includeCountry && (
        <DetailRow
          name={t('COMMON.COUNTRY')}
          value={getAddressField(address, 'country')}
        />
      )}
      {includeProofOfAddress && documentData && (
        <DetailRow
          name={
            isPersonalAddress
              ? t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_PERSONAL_ADDRESS_PROOF')
              : t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_LEGAL_ADDRESS_PROOF')
          }
          value={
            <DocumentLink
              link={selectDocumentType(documentType, documentData.documents)}
            />
          }
        >
          <DocumentsMenu
            documentType={documentType}
            clientId={documentData.id}
            documents={Boolean(
              selectDocumentType(documentType, documentData.documents)
            )}
          />
        </DetailRow>
      )}
    </>
  );
};

AddressDetailBox.propTypes = {
  address: PropTypes.object,
  includeCountry: PropTypes.bool,
  includeProofOfAddress: PropTypes.bool,
  includeMunicipality: PropTypes.bool,
  documentData: PropTypes.object
};
export default AddressDetailBox;
